import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesAPI } from '../../shared/services/api';
import toast from 'react-hot-toast';

const RoleManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteDialog, setDeleteDialog] = useState({ open: false, role: null });
  const [cloneDialog, setCloneDialog] = useState({ open: false, role: null, name: '' });

  // Fetch roles
  const { data: rolesData, isLoading, error } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesAPI.getRoles,
  });

  // Delete role mutation
  const deleteMutation = useMutation({
    mutationFn: rolesAPI.deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      setDeleteDialog({ open: false, role: null });
      toast.success('Role deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete role');
    },
  });

  // Clone role mutation
  const cloneMutation = useMutation({
    mutationFn: ({ id, name }) => rolesAPI.cloneRole(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      setCloneDialog({ open: false, role: null, name: '' });
      toast.success('Role cloned successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to clone role');
    },
  });

  const getUserTypeColor = (userType) => {
    const colors = {
      admin: 'error',
      member: 'primary',
      corporate: 'success',
      provider: 'warning',
      intermediary: 'info',
      insurer: 'secondary',
      irda: 'default'
    };
    return colors[userType] || 'default';
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load roles: {error.response?.data?.message || error.message}
      </Alert>
    );
  }

  const roles = rolesData?.data?.data || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Role Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/roles/create')}
        >
          Create Role
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" paragraph>
        Manage user roles and permissions across all portals
      </Typography>

      {/* Roles Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>User Type</TableCell>
                {/* <TableCell>Portal Access</TableCell> */}
                <TableCell>Permissions</TableCell>
                {/* <TableCell>System Role</TableCell> */}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No roles found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {role.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {role.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.userType}
                        size="small"
                        color={getUserTypeColor(role.userType)}
                        variant="outlined"
                      />
                    </TableCell>
                    {/* <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {role.portalAccess?.slice(0, 3).map((portal) => (
                          <Chip
                            key={portal}
                            label={portal}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {role.portalAccess?.length > 3 && (
                          <Chip
                            label={`+${role.portalAccess.length - 3}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell> */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        { role.hierarchicalPermissions ? (Object.keys(role.hierarchicalPermissions)?.length) : Object.keys(role.permissions)?.length} permissions
                      </Typography>
                    </TableCell>
                    {/* <TableCell>
                      {role.isSystemRole && (
                        <Chip
                          label="System"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </TableCell> */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit Role">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/roles/edit/${role._id}`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clone Role">
                          <IconButton
                            size="small"
                            onClick={() => setCloneDialog({ open: true, role, name: `${role.name} (Copy)` })}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                        {!role.isSystemRole && (
                          <Tooltip title="Delete Role">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialog({ open: true, role })}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, role: null })}
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role "{deleteDialog.role?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, role: null })}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate(deleteDialog.role._id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Role Dialog */}
      <Dialog
        open={cloneDialog.open}
        onClose={() => setCloneDialog({ open: false, role: null, name: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clone Role</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Create a copy of "{cloneDialog.role?.name}" with a new name:
          </Typography>
          <TextField
            fullWidth
            label="New Role Name"
            value={cloneDialog.name}
            onChange={(e) => setCloneDialog({ ...cloneDialog, name: e.target.value })}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialog({ open: false, role: null, name: '' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => cloneMutation.mutate({ id: cloneDialog.role._id, name: cloneDialog.name })}
            disabled={cloneMutation.isPending || !cloneDialog.name.trim()}
          >
            {cloneMutation.isPending ? <CircularProgress size={20} /> : 'Clone Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
